import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Org Chart modal — AI-inferred reporting structure drawn as a real org
 * chart: photo/name/title cards connected by elbow lines, laid out top-down.
 * Compact layout: a manager's individual contributors are grouped into one
 * vertical stack (even when sub-managers sit alongside them), which is what
 * keeps a ~200-person company on one readable canvas. Pan by dragging, zoom
 * with the trackpad/wheel (delta-proportional, so Mac trackpads don't fly),
 * snap to Top 2/3/4 levels from the toolbar, click a card for the person's
 * full profile + chart actions. Amendments pin as user-confirmed facts that
 * survive regeneration.
 */

interface OrgNode {
  id: number;
  candidate_id: number;
  parent_candidate_id: number | null;
  level: number | null;
  function: string | null;
  inferred_area: string | null;
  confidence: 'high' | 'medium' | 'low' | null;
  reason: string | null;
  unplaced: boolean;
  unplaced_why: string | null;
  source: 'ai' | 'user';
  pinned: boolean;
  first_name: string | null;
  last_name: string | null;
  current_position: string | null;
  company_title: string | null;   // their role at THIS company (current_position can be a concurrent side gig)
  li_profile_url: string | null;
  photo_url: string | null;
  location_full: string | null;
}

// The title the chart shows: their role at this company when known.
const chartTitle = (n: OrgNode) => n.company_title || n.current_position;

interface Chart {
  id: number;
  status: 'generating' | 'ready' | 'failed';
  company_name: string | null;
  mode: 'full' | 'quick' | null;
  notes: string[] | null;
  error: string | null;
  generated_at: string | null;
  nodes: OrgNode[];
}

interface Profile {
  id: number;
  first_name: string | null;
  last_name: string | null;
  current_position: string | null;
  current_company: string | null;
  headline: string | null;
  summary: string | null;
  location_full: string | null;
  position_history: any[] | null;
  education_history: any[] | null;
  all_skills: string[] | string | null;
  email: string | null;
  personal_email: string | null;
  phone: string | null;
  photo_url: string | null;
  li_profile_url: string | null;
}

const FUNCTION_BAR: Record<string, string> = {
  Executive: '#334155', Sales: '#2563eb', Marketing: '#db2777', Engineering: '#4f46e5',
  Product: '#7c3aed', 'Customer Success': '#0d9488', 'Professional Services': '#0891b2',
  Finance: '#d97706', 'People/HR': '#e11d48', Operations: '#ea580c', Legal: '#57534e',
  'IT/Security': '#059669', Other: '#6b7280',
};

const name = (n: { first_name: string | null; last_name: string | null }) =>
  `${n.first_name || ''} ${n.last_name || ''}`.trim() || 'Unknown';
const initials = (n: { first_name: string | null; last_name: string | null }) =>
  `${(n.first_name || '?')[0] || ''}${(n.last_name || '')[0] || ''}`.toUpperCase();

// ---- layout constants ----
const CARD_W = 224;
const CARD_H = 86;
const SCARD_H = 64;          // stacked (IC) card height
const H_GAP = 36;
const V_GAP = 64;
const STACK_GAP = 10;
const STACK_INDENT = 30;     // stack offset under its own manager
const STACK_TOP = 34;
const SPINE = 16;            // spine inset for grouped IC stacks

interface PlacedCard { node: OrgNode; x: number; y: number; stacked: boolean; hasKids: boolean; kidCount: number; }

export function computeLayout(roots: OrgNode[], childrenOf: (cid: number) => OrgNode[]) {
  const cards: PlacedCard[] = [];
  const edges: string[] = [];
  const sizes = new Map<number, { w: number; h: number }>();

  const countDesc = (n: OrgNode): number =>
    childrenOf(n.candidate_id).reduce((a, k) => a + 1 + countDesc(k), 0);

  const split = (kids: OrgNode[]) => {
    const branches = kids.filter(k => childrenOf(k.candidate_id).length > 0);
    const leaves = kids.filter(k => childrenOf(k.candidate_id).length === 0);
    return { branches, leaves };
  };

  const stackH = (count: number) => count * (SCARD_H + STACK_GAP) - STACK_GAP;

  const measure = (n: OrgNode): { w: number; h: number } => {
    const cached = sizes.get(n.candidate_id);
    if (cached) return cached;
    const kids = childrenOf(n.candidate_id);
    let out: { w: number; h: number };
    if (!kids.length) {
      out = { w: CARD_W, h: CARD_H };
    } else {
      const { branches, leaves } = split(kids);
      if (!branches.length) {
        // Pure leaf team: manager card with the stack below it.
        out = { w: STACK_INDENT + CARD_W, h: CARD_H + STACK_TOP + stackH(leaves.length) };
      } else {
        // Branch blocks side by side; 2+ leaf ICs are grouped into one
        // stacked column so same-team people don't spread the row.
        const blocks: { w: number; h: number }[] = branches.map(measure);
        if (leaves.length === 1) blocks.push({ w: CARD_W, h: CARD_H });
        if (leaves.length >= 2) blocks.push({ w: SPINE + CARD_W, h: stackH(leaves.length) });
        const rowW = blocks.reduce((a, m) => a + m.w, 0) + (blocks.length - 1) * H_GAP;
        out = { w: Math.max(CARD_W, rowW), h: CARD_H + V_GAP + Math.max(...blocks.map(m => m.h)) };
      }
    }
    sizes.set(n.candidate_id, out);
    return out;
  };

  // Places the subtree block with its top-left at (x, y); returns the center
  // x of the node's own card so the parent can route a connector to it.
  const place = (n: OrgNode, x: number, y: number): number => {
    const kids = childrenOf(n.candidate_id);
    const m = measure(n);
    if (!kids.length) {
      const cx = x + (m.w - CARD_W) / 2;
      cards.push({ node: n, x: cx, y, stacked: false, hasKids: false, kidCount: 0 });
      return cx + CARD_W / 2;
    }
    const { branches, leaves } = split(kids);
    if (!branches.length) {
      // Pure leaf team: manager + indented stack joined by a left spine.
      cards.push({ node: n, x, y, stacked: false, hasKids: true, kidCount: countDesc(n) });
      const spineX = x + SPINE;
      let cy = y + CARD_H + STACK_TOP;
      let lastStubY = y + CARD_H;
      leaves.forEach(k => {
        const stubY = cy + SCARD_H / 2;
        cards.push({ node: k, x: x + STACK_INDENT, y: cy, stacked: true, hasKids: false, kidCount: 0 });
        edges.push(`M ${spineX} ${stubY} H ${x + STACK_INDENT}`);
        lastStubY = stubY;
        cy += SCARD_H + STACK_GAP;
      });
      edges.push(`M ${spineX} ${y + CARD_H} V ${lastStubY}`);
      return x + CARD_W / 2;
    }
    // Mixed/branch row: sub-manager blocks, then a lone leaf as a card, then
    // 2+ leaves grouped as a stacked column hanging off the bus.
    type Block = { kind: 'branch'; node: OrgNode; w: number } | { kind: 'leafCard'; node: OrgNode; w: number } | { kind: 'leafStack'; nodes: OrgNode[]; w: number };
    const blocks: Block[] = branches.map(b => ({ kind: 'branch' as const, node: b, w: measure(b).w }));
    if (leaves.length === 1) blocks.push({ kind: 'leafCard', node: leaves[0], w: CARD_W });
    if (leaves.length >= 2) blocks.push({ kind: 'leafStack', nodes: leaves, w: SPINE + CARD_W });
    const rowW = blocks.reduce((a, b) => a + b.w, 0) + (blocks.length - 1) * H_GAP;
    let cx = x + (m.w - rowW) / 2;
    const childTop = y + CARD_H + V_GAP;
    const busY = childTop - V_GAP / 2;
    const centers: number[] = [];
    blocks.forEach(b => {
      if (b.kind === 'branch') {
        centers.push(place(b.node, cx, childTop));
      } else if (b.kind === 'leafCard') {
        cards.push({ node: b.node, x: cx, y: childTop, stacked: false, hasKids: false, kidCount: 0 });
        const c = cx + CARD_W / 2;
        centers.push(c);
        edges.push(`M ${c} ${busY} V ${childTop}`);
      } else {
        // Grouped IC stack: spine drops from the bus, stubs into each card.
        const spineX = cx;
        let cy = childTop;
        let lastStubY = busY;
        b.nodes.forEach(k => {
          const stubY = cy + SCARD_H / 2;
          cards.push({ node: k, x: cx + SPINE, y: cy, stacked: true, hasKids: false, kidCount: 0 });
          edges.push(`M ${spineX} ${stubY} H ${cx + SPINE}`);
          lastStubY = stubY;
          cy += SCARD_H + STACK_GAP;
        });
        edges.push(`M ${spineX} ${busY} V ${lastStubY}`);
        centers.push(spineX);
      }
      cx += b.w + H_GAP;
    });
    // Connect branch children to the bus (leafCard/leafStack already did).
    blocks.forEach((b, i) => {
      if (b.kind === 'branch') edges.push(`M ${centers[i]} ${busY} V ${childTop}`);
    });
    const mid = (centers[0] + centers[centers.length - 1]) / 2;
    const cardX = Math.min(Math.max(mid - CARD_W / 2, x), x + m.w - CARD_W);
    cards.push({ node: n, x: cardX, y, stacked: false, hasKids: true, kidCount: countDesc(n) });
    edges.push(`M ${cardX + CARD_W / 2} ${y + CARD_H} V ${busY}`);
    if (centers.length > 1) edges.push(`M ${Math.min(...centers)} ${busY} H ${Math.max(...centers)}`);
    return cardX + CARD_W / 2;
  };

  let x = 0;
  let totalH = 0;
  roots.forEach(r => {
    const m = measure(r);
    place(r, x, 0);
    x += m.w + H_GAP * 2;
    totalH = Math.max(totalH, m.h);
  });
  return { cards, edges, width: Math.max(x - H_GAP * 2, CARD_W), height: totalH };
}

// ---- date helpers for the profile popup ----
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (d: any) => d && d.year ? `${d.month ? MONTHS[(d.month - 1) % 12] + ' ' : ''}${d.year}` : null;
const fmtRange = (sed: any) => {
  if (!sed) return '';
  const s = fmtDate(sed.start);
  const e = sed.end === null ? 'Present' : fmtDate(sed.end);
  return [s, e].filter(Boolean).join(' – ');
};
const skillArray = (s: Profile['all_skills']): string[] => {
  if (Array.isArray(s)) return s.map(String);
  if (typeof s === 'string') { try { const a = JSON.parse(s); return Array.isArray(a) ? a.map(String) : s.split(','); } catch { return s.split(','); } }
  return [];
};

// companyId/companyName scope the chart to one company's candidates within
// the project (launched from the companies tab); without them the chart is
// project-wide (single-company projects, launched from the candidates tab).
const OrgChartModal: React.FC<{ projectId: number; companyId?: number; companyName?: string; onClose: () => void }> = ({ projectId, companyId, companyName, onClose }) => {
  const [chart, setChart] = useState<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genMessage, setGenMessage] = useState('');
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [picker, setPicker] = useState<{ node: OrgNode; query: string } | null>(null);
  const [profileFor, setProfileFor] = useState<OrgNode | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showUnplaced, setShowUnplaced] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState({ z: 0.5, px: 0, py: 0 });
  // Dragging a card onto another card re-parents the person under them.
  const [cardDrag, setCardDrag] = useState<{ node: OrgNode; sx: number; sy: number; dx: number; dy: number } | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const cardDragMovedRef = useRef(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const movedRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const didFitRef = useRef(false);
  const refitRef = useRef(false);

  const headers = useMemo(() => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  }), []);

  const fetchChart = useCallback(async () => {
    try {
      const res = await fetch(`/api/org-chart/${projectId}${companyId ? `?companyId=${companyId}` : ''}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setChart(data.chart);
      setError(null);
      return data.chart as Chart | null;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [projectId, companyId, headers]);

  useEffect(() => {
    fetchChart();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchChart]);

  useEffect(() => {
    if (chart?.status !== 'generating' || generating) return;
    const t = setInterval(async () => {
      const c = await fetchChart();
      if (c && c.status !== 'generating') clearInterval(t);
    }, 5000);
    return () => clearInterval(t);
  }, [chart?.status, generating, fetchChart]);

  const generate = async (mode: 'full' | 'quick') => {
    if (chart?.nodes?.length && !window.confirm(
      'Regenerate the org chart? Your pinned amendments are kept and re-applied; everything else is re-inferred from the latest data. Takes a few minutes.')) return;
    setGenerating(true);
    setGenMessage('Starting…');
    setError(null);
    try {
      const res = await fetch('/api/org-chart/generate', { method: 'POST', headers, body: JSON.stringify({ projectId, companyId, mode }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start');
      setGenMessage(`Reading ${data.total.toLocaleString()} profiles and drafting the structure — typically 2–4 minutes…`);
      pollRef.current = setInterval(async () => {
        try {
          const p = await fetch(`/api/org-chart/progress/${data.operationId}`, { headers });
          const op = await p.json();
          if (!p.ok) throw new Error(op.error || 'Lost track of the run');
          if (op.status === 'running' && op.chunks) setGenMessage(`Drafting the structure… (${op.chunks} chunks written)`);
          if (op.status === 'done' || op.status === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current);
            setGenerating(false);
            if (op.status === 'failed') setError(op.error || 'Generation failed');
            didFitRef.current = false;
            await fetchChart();
          }
        } catch (e: any) {
          if (pollRef.current) clearInterval(pollRef.current);
          setGenerating(false);
          setError(e.message);
        }
      }, 3000);
    } catch (e: any) {
      setGenerating(false);
      setError(e.message);
    }
  };

  const patchNode = async (nodeId: number, body: any) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/org-chart/node/${nodeId}`, { method: 'PATCH', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      await fetchChart();
    } catch (e: any) {
      window.alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openProfile = async (n: OrgNode) => {
    setProfileFor(n);
    setProfile(null);
    setProfileLoading(true);
    try {
      const res = await fetch(`/api/org-chart/profile/${n.candidate_id}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load profile');
      setProfile(data.profile);
    } catch (e: any) {
      window.alert(e.message);
      setProfileFor(null);
    } finally {
      setProfileLoading(false);
    }
  };

  // ---- tree data ----
  const placed = useMemo(() => (chart?.nodes || []).filter(n => !n.unplaced), [chart]);
  const unplaced = useMemo(() => (chart?.nodes || []).filter(n => n.unplaced), [chart]);
  const byCandidate = useMemo(() => new Map(placed.map(n => [n.candidate_id, n])), [placed]);
  const childMap = useMemo(() => {
    const m = new Map<number, OrgNode[]>();
    for (const n of placed) {
      if (n.parent_candidate_id == null || !byCandidate.has(n.parent_candidate_id)) continue;
      if (!m.has(n.parent_candidate_id)) m.set(n.parent_candidate_id, []);
      m.get(n.parent_candidate_id)!.push(n);
    }
    for (const kids of Array.from(m.values())) {
      kids.sort((a, b) => (a.function || '').localeCompare(b.function || '') || name(a).localeCompare(name(b)));
    }
    return m;
  }, [placed, byCandidate]);
  const roots = useMemo(
    () => placed.filter(n => n.parent_candidate_id == null || !byCandidate.has(n.parent_candidate_id)),
    [placed, byCandidate]);

  // depth of each node from its root (1-based), for the Top-N presets
  const depths = useMemo(() => {
    const d = new Map<number, number>();
    const walk = (n: OrgNode, depth: number) => {
      if (d.has(n.candidate_id)) return;
      d.set(n.candidate_id, depth);
      (childMap.get(n.candidate_id) || []).forEach(k => walk(k, depth + 1));
    };
    roots.forEach(r => walk(r, 1));
    return d;
  }, [roots, childMap]);

  const maxDepth = useMemo(() => Math.max(1, ...Array.from(depths.values())), [depths]);

  const applyDepthPreset = (n: number | null) => {
    refitRef.current = true;
    if (n == null) { setCollapsed(new Set()); return; }
    const next = new Set<number>();
    for (const [cid, d] of Array.from(depths.entries())) {
      if (d >= n && (childMap.get(cid) || []).length > 0) next.add(cid);
    }
    setCollapsed(next);
  };

  const layout = useMemo(() => {
    if (!roots.length) return { cards: [] as PlacedCard[], edges: [] as string[], width: 0, height: 0 };
    const childrenOf = (cid: number) => collapsed.has(cid) ? [] : (childMap.get(cid) || []);
    return computeLayout(roots, childrenOf);
  }, [roots, childMap, collapsed]);

  const cardByCandidate = useMemo(() => new Map(layout.cards.map(c => [c.node.candidate_id, c])), [layout]);

  // ---- viewport ----
  const fit = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp || !layout.width) return;
    const pad = 48;
    const z = Math.min((vp.clientWidth - pad) / layout.width, (vp.clientHeight - pad) / layout.height, 1);
    const zc = Math.max(z, 0.08);
    setView({
      z: zc,
      px: (vp.clientWidth - layout.width * zc) / 2,
      py: Math.max((vp.clientHeight - layout.height * zc) / 2, 16),
    });
  }, [layout.width, layout.height]);

  useEffect(() => {
    if (layout.width > 0 && chart?.status === 'ready' && (!didFitRef.current || refitRef.current)) {
      didFitRef.current = true;
      refitRef.current = false;
      requestAnimationFrame(fit);
    }
  }, [layout.width, layout.height, chart?.status, fit]);

  const zoomAt = useCallback((factor: number, cx?: number, cy?: number) => {
    setView(v => {
      const vp = viewportRef.current;
      const ox = cx ?? (vp ? vp.clientWidth / 2 : 0);
      const oy = cy ?? (vp ? vp.clientHeight / 2 : 0);
      const z = Math.min(Math.max(v.z * factor, 0.06), 2);
      const scale = z / v.z;
      return { z, px: ox - (ox - v.px) * scale, py: oy - (oy - v.py) * scale };
    });
  }, []);

  // Delta-proportional zoom: Mac trackpads fire a stream of small wheel
  // events, so a fixed step per event compounds way too fast. Pinch gestures
  // arrive as ctrlKey wheel events with their own scale.
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const coeff = e.ctrlKey ? 0.008 : 0.0022;
    const factor = Math.min(Math.max(Math.exp(-e.deltaY * coeff), 0.8), 1.25);
    const rect = viewportRef.current?.getBoundingClientRect();
    zoomAt(factor, rect ? e.clientX - rect.left : undefined, rect ? e.clientY - rect.top : undefined);
  };

  // Everyone below a node in the chart — invalid drop targets (a person
  // can't report into their own chain).
  const descendantsOf = useCallback((cid: number) => {
    const out = new Set<number>();
    const walk = (id: number) => (childMap.get(id) || []).forEach(k => { out.add(k.candidate_id); walk(k.candidate_id); });
    walk(cid);
    return out;
  }, [childMap]);

  const onMouseDown = (e: React.MouseEvent) => {
    dragRef.current = { x: e.clientX, y: e.clientY, px: view.px, py: view.py };
    movedRef.current = false;
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (cardDrag) {
      const dxs = e.clientX - cardDrag.sx, dys = e.clientY - cardDrag.sy;
      if (Math.abs(dxs) + Math.abs(dys) > 4) cardDragMovedRef.current = true;
      setCardDrag(d => d ? { ...d, dx: dxs / view.z, dy: dys / view.z } : d);
      // hit-test the cursor against the other cards in canvas coordinates
      const rect = viewportRef.current?.getBoundingClientRect();
      if (rect) {
        const cx = (e.clientX - rect.left - view.px) / view.z;
        const cy = (e.clientY - rect.top - view.py) / view.z;
        const invalid = descendantsOf(cardDrag.node.candidate_id);
        const hit = layout.cards.find(c =>
          c.node.candidate_id !== cardDrag.node.candidate_id &&
          !invalid.has(c.node.candidate_id) &&
          cx >= c.x && cx <= c.x + CARD_W &&
          cy >= c.y && cy <= c.y + (c.stacked ? SCARD_H : CARD_H));
        setDropTarget(hit ? hit.node.candidate_id : null);
      }
      return;
    }
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) movedRef.current = true;
    setView(v => ({ ...v, px: d.px + dx, py: d.py + dy }));
  };
  const endDrag = () => {
    if (cardDrag) {
      if (cardDragMovedRef.current && dropTarget != null && dropTarget !== cardDrag.node.parent_candidate_id) {
        patchNode(cardDrag.node.id, { parentCandidateId: dropTarget });
      }
      setCardDrag(null);
      setDropTarget(null);
    }
    dragRef.current = null;
  };

  const centerOn = useCallback((cid: number) => {
    const c = cardByCandidate.get(cid);
    const vp = viewportRef.current;
    if (!c || !vp) return;
    setView(v => {
      const z = Math.max(v.z, 0.6);
      return {
        z,
        px: vp.clientWidth / 2 - (c.x + CARD_W / 2) * z,
        py: vp.clientHeight / 3 - c.y * z,
      };
    });
  }, [cardByCandidate]);

  // ---- search ----
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return new Set<number>();
    return new Set(placed
      .filter(n => name(n).toLowerCase().includes(q) || (chartTitle(n) || '').toLowerCase().includes(q))
      .map(n => n.candidate_id));
  }, [search, placed]);

  useEffect(() => {
    if (!matches.size) return;
    const first = matches.values().next().value as number;
    setCollapsed(prev => {
      const next = new Set(prev);
      for (const cid of Array.from(matches)) {
        let cur = byCandidate.get(cid);
        const guard = new Set<number>();
        while (cur && cur.parent_candidate_id != null && !guard.has(cur.candidate_id)) {
          guard.add(cur.candidate_id);
          next.delete(cur.parent_candidate_id);
          cur = byCandidate.get(cur.parent_candidate_id);
        }
      }
      return next;
    });
    const t = setTimeout(() => centerOn(first), 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toggleCollapse = (cid: number) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(cid)) next.delete(cid); else next.add(cid);
    return next;
  });

  const pickerOptions = useMemo(() => {
    if (!picker) return [];
    const q = picker.query.trim().toLowerCase();
    return placed
      .filter(n => n.candidate_id !== picker.node.candidate_id)
      .filter(n => !q || name(n).toLowerCase().includes(q) || (chartTitle(n) || '').toLowerCase().includes(q))
      .slice(0, 30);
  }, [picker, placed]);

  const confCounts = useMemo(() => {
    const c = { high: 0, medium: 0, low: 0, pinned: 0 };
    for (const n of placed) {
      if (n.pinned) c.pinned += 1;
      else if (n.confidence) c[n.confidence] += 1;
    }
    return c;
  }, [placed]);

  const Avatar: React.FC<{ n: { first_name: string | null; last_name: string | null; photo_url: string | null }; size: number }> = ({ n, size }) => (
    n.photo_url
      ? <img src={n.photo_url} alt="" className="rounded-full object-cover shrink-0" style={{ width: size, height: size }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
      : <span className="rounded-full bg-gray-200 text-gray-600 font-semibold flex items-center justify-center shrink-0"
          style={{ width: size, height: size, fontSize: size * 0.34 }}>{initials(n)}</span>
  );

  const renderCard = (c: PlacedCard) => {
    const n = c.node;
    const isSel = profileFor?.candidate_id === n.candidate_id;
    const isMatch = matches.has(n.candidate_id);
    const dim = matches.size > 0 && !isMatch;
    const bar = FUNCTION_BAR[n.function || 'Other'] || FUNCTION_BAR.Other;
    const h = c.stacked ? SCARD_H : CARD_H;
    const isCollapsed = collapsed.has(n.candidate_id);
    const isDragging = cardDrag?.node.candidate_id === n.candidate_id && cardDragMovedRef.current;
    const isDrop = dropTarget === n.candidate_id;
    return (
      <div
        key={n.candidate_id}
        className={`absolute bg-white rounded-lg border shadow-sm select-none ${isDragging ? 'opacity-75 shadow-xl cursor-grabbing' : 'cursor-pointer transition-shadow hover:shadow-md'} ${isDrop ? 'ring-2 ring-emerald-500' : isSel ? 'ring-2 ring-indigo-500' : isMatch ? 'ring-2 ring-amber-400' : 'border-gray-200'} ${dim && !isDragging ? 'opacity-40' : ''}`}
        style={{
          left: c.x, top: c.y, width: CARD_W, height: h,
          ...(isDragging ? { transform: `translate(${cardDrag!.dx}px, ${cardDrag!.dy}px)`, zIndex: 30, pointerEvents: 'none' as const } : {}),
        }}
        onMouseDown={e => {
          e.stopPropagation();
          cardDragMovedRef.current = false;
          setCardDrag({ node: n, sx: e.clientX, sy: e.clientY, dx: 0, dy: 0 });
        }}
        onDoubleClick={e => e.stopPropagation()}
        onClick={e => { e.stopPropagation(); if (!cardDragMovedRef.current) openProfile(n); }}
        title="Click for full profile · drag onto someone to change manager"
      >
        <div className="rounded-t-lg" style={{ height: 4, background: bar }} />
        <div className="flex items-center gap-2 px-2.5" style={{ height: h - 4 }}>
          <Avatar n={n} size={c.stacked ? 30 : 40} />
          <div className="min-w-0 flex-1">
            <p className={`font-semibold text-gray-900 truncate leading-tight ${c.stacked ? 'text-[11px]' : 'text-[13px]'}`}>{name(n)}</p>
            <p className={`text-gray-500 truncate leading-tight ${c.stacked ? 'text-[9.5px]' : 'text-[11px]'}`} title={chartTitle(n) || ''}>{chartTitle(n)}</p>
            {!c.stacked && (
              <p className="text-[9.5px] truncate leading-tight" style={{ color: bar }}>
                {n.function}{n.inferred_area ? ` · ${n.inferred_area}` : ''}
              </p>
            )}
          </div>
          <div className="flex flex-col items-center gap-0.5 shrink-0">
            {n.pinned && <span title="Confirmed by you — kept on regeneration" className="text-[10px] leading-none">📌</span>}
            {!n.pinned && n.confidence === 'medium' && <span title={n.reason || 'Reasonable guess — worth a glance'} className="w-2 h-2 rounded-full bg-amber-400" />}
            {!n.pinned && n.confidence === 'low' && <span title={n.reason || 'Mostly guessing — check this one'} className="w-2 h-2 rounded-full bg-red-400" />}
          </div>
        </div>
        {c.hasKids && (
          <button
            onClick={e => { e.stopPropagation(); toggleCollapse(n.candidate_id); }}
            className="absolute left-1/2 -translate-x-1/2 -bottom-2.5 px-1.5 h-5 min-w-[20px] rounded-full bg-white border border-gray-300 text-[9px] text-gray-600 shadow-sm hover:bg-gray-50 flex items-center justify-center leading-none"
            title={isCollapsed ? `Show ${c.kidCount} people` : 'Collapse this team'}
          >{isCollapsed ? `+${c.kidCount}` : '−'}</button>
        )}
      </div>
    );
  };

  // ---- profile popup ----
  const renderProfile = () => {
    if (!profileFor) return null;
    const n = profileFor;
    const p = profile;
    const manager = n.parent_candidate_id != null ? byCandidate.get(n.parent_candidate_id) : null;
    const skills = p ? skillArray(p.all_skills).filter(Boolean).slice(0, 18) : [];
    const history = (p?.position_history || []).filter((e: any) => e && (e.title || e.companyName)).slice(0, 12);
    const education = (p?.education_history || []).filter((e: any) => e && e.schoolName).slice(0, 4);
    const bar = FUNCTION_BAR[n.function || 'Other'] || FUNCTION_BAR.Other;
    return (
      <div className="fixed inset-0 bg-black/30 z-[60] flex items-center justify-center p-4" onClick={() => setProfileFor(null)}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
          <div style={{ height: 5, background: bar }} />
          <div className="px-5 py-4 border-b flex items-start gap-3">
            <Avatar n={{ first_name: n.first_name, last_name: n.last_name, photo_url: n.photo_url }} size={52} />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900 text-base leading-tight">{name(n)}</p>
              <p className="text-sm text-gray-600 leading-tight">{chartTitle(n)}{p?.current_company ? ` · ${p.current_company}` : ''}</p>
              {n.company_title && n.current_position && n.company_title !== n.current_position && (
                <p className="text-xs text-gray-400 mt-0.5">Also: {n.current_position} (concurrent role elsewhere)</p>
              )}
              {(p?.location_full || n.location_full) && <p className="text-xs text-gray-400 mt-0.5">{p?.location_full || n.location_full}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {n.li_profile_url && (
                <a href={n.li_profile_url} target="_blank" rel="noreferrer"
                  className="px-2 py-1 text-[11px] bg-[#0a66c2] text-white rounded hover:opacity-90">LinkedIn ↗</a>
              )}
              <button onClick={() => setProfileFor(null)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 text-lg">×</button>
            </div>
          </div>

          {/* Chart placement + actions */}
          <div className="px-5 py-3 bg-gray-50 border-b text-xs flex items-center gap-2 flex-wrap">
            {n.unplaced ? (
              <span className="text-gray-600">Not placed in the org{n.unplaced_why ? ` — ${n.unplaced_why}` : ''}</span>
            ) : (
              <>
                <span className="text-gray-600">
                  Reports to <span className="font-medium text-gray-900">{manager ? name(manager) : 'no one (top of chart)'}</span>
                </span>
                <span className="px-1.5 py-0.5 rounded text-[10px] text-white" style={{ background: bar }}>
                  {n.function}{n.inferred_area ? ` · ${n.inferred_area}` : ''}
                </span>
                {n.pinned
                  ? <span className="text-indigo-600">📌 confirmed by you</span>
                  : n.confidence && n.confidence !== 'high'
                    ? <span className={n.confidence === 'low' ? 'text-red-500' : 'text-amber-600'} title={n.reason || ''}>
                        {n.confidence} confidence{n.reason ? ` — ${n.reason}` : ''}
                      </span>
                    : null}
              </>
            )}
            <span className="ml-auto flex items-center gap-1">
              <button onClick={() => { setProfileFor(null); setPicker({ node: n, query: '' }); }} disabled={saving}
                className="px-2 py-1 text-[11px] bg-indigo-600 text-white rounded hover:bg-indigo-700">
                {n.unplaced ? 'Place under…' : 'Change manager'}
              </button>
              {!n.unplaced && (
                <button onClick={() => { if (window.confirm(`Remove ${name(n)} from the chart? Their reports move up to their manager.`)) { setProfileFor(null); patchNode(n.id, { unplace: true }); } }} disabled={saving}
                  className="px-2 py-1 text-[11px] bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Remove from chart</button>
              )}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {profileLoading && <p className="text-sm text-gray-400">Loading profile…</p>}
            {p && (
              <>
                {p.headline && <p className="text-sm text-gray-800 font-medium">{p.headline}</p>}
                {p.summary && <p className="text-xs text-gray-600 whitespace-pre-line">{String(p.summary).slice(0, 1200)}{String(p.summary).length > 1200 ? '…' : ''}</p>}
                {(p.email || p.personal_email || p.phone) && (
                  <p className="text-xs text-gray-500">
                    {[p.email || p.personal_email, p.phone].filter(Boolean).join(' · ')}
                  </p>
                )}
                {history.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Career history</p>
                    <div className="space-y-1.5">
                      {history.map((e: any, i: number) => (
                        <div key={i} className="flex items-baseline gap-2 text-xs">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 relative top-[1px] ${e.startEndDate?.end === null ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                          <span className="font-medium text-gray-800">{e.title || '—'}</span>
                          <span className="text-gray-500 truncate">{e.companyName}</span>
                          <span className="text-gray-400 whitespace-nowrap ml-auto">{fmtRange(e.startEndDate)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {education.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Education</p>
                    {education.map((e: any, i: number) => (
                      <p key={i} className="text-xs text-gray-700">{e.schoolName}{e.degreeName ? ` — ${e.degreeName}` : ''}{e.fieldOfStudy ? `, ${e.fieldOfStudy}` : ''}</p>
                    ))}
                  </div>
                )}
                {skills.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Skills</p>
                    <div className="flex flex-wrap gap-1">
                      {skills.map((s, i) => <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">{s}</span>)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3">
      <div className="bg-white rounded-xl shadow-2xl w-full h-full max-w-[1800px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Org Chart{(chart?.company_name || companyName) ? ` — ${chart?.company_name || companyName}` : ''}
          </h2>
          {chart?.mode === 'quick' && (
            <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-full" title="Generated in quick mode — reporting lines from titles and tenure only. Regenerate as a full chart for the richest inference.">quick</span>
          )}
          {chart?.generated_at && (
            <span className="text-xs text-gray-400">generated {new Date(chart.generated_at).toLocaleString()}</span>
          )}
          <span className="ml-auto flex items-center gap-2">
            {chart?.status === 'ready' && (
              <>
                {chart.mode === 'quick' && (
                  <button onClick={() => generate('full')} disabled={generating || saving}
                    className="px-3 py-1.5 text-xs bg-white border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50 disabled:opacity-50"
                    title="Re-run with full profiles (role descriptions, summaries, skills) for the richest inference">
                    Upgrade to full chart
                  </button>
                )}
                <button onClick={() => generate(chart.mode === 'quick' ? 'quick' : 'full')} disabled={generating || saving}
                  className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400">
                  {generating ? '⏳ Working…' : '↻ Regenerate'}
                </button>
              </>
            )}
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 text-xl">×</button>
          </span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col relative">
          {loading && <div className="p-8 text-sm text-gray-500">Loading…</div>}

          {!loading && error && !generating && (
            <div className="m-5 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
          )}

          {!loading && (generating || chart?.status === 'generating') && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-8">
              <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full" />
              <p className="text-sm text-gray-700 font-medium">Building the org chart…</p>
              <p className="text-xs text-gray-500 max-w-md">{genMessage || 'The AI is reading every profile — title, tenure, role descriptions, skills — and inferring the most plausible reporting structure. Typically 2–4 minutes.'}</p>
            </div>
          )}

          {!loading && !generating && chart?.status !== 'generating' && !chart?.nodes?.length && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-8">
              <p className="text-sm text-gray-700 font-medium">No org chart yet for {companyName || 'this project'}</p>
              <p className="text-xs text-gray-500 max-w-md">
                The AI works out who most plausibly reports to whom and flags anyone who isn't really part of the org —
                duplicates, board members, similarly-named companies. You can then correct any edge (drag a card onto a
                new manager), and your corrections are kept when the chart is refreshed.
              </p>
              {chart?.status === 'failed' && chart.error && (
                <p className="text-xs text-red-600">Last attempt failed: {chart.error}</p>
              )}
              <div className="mt-2 flex items-stretch gap-3">
                <button onClick={() => generate('full')} className="w-56 px-4 py-3 text-left bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  <span className="block text-sm font-medium">Full org chart</span>
                  <span className="block text-[11px] opacity-80 mt-0.5">Reads complete profiles — role descriptions, summaries, skills. The richest inference.</span>
                </button>
                <button onClick={() => generate('quick')} className="w-56 px-4 py-3 text-left bg-white border border-indigo-300 text-indigo-800 rounded-lg hover:bg-indigo-50">
                  <span className="block text-sm font-medium">Quick reporting lines</span>
                  <span className="block text-[11px] text-indigo-600/80 mt-0.5">Titles and tenure only — a fast seniority pyramid of who sits under whom. Cheaper and quicker.</span>
                </button>
              </div>
            </div>
          )}

          {!loading && !generating && chart?.status === 'ready' && placed.length > 0 && (
            <>
              {/* Toolbar */}
              <div className="flex items-center gap-3 px-5 py-2 border-b bg-gray-50 text-xs shrink-0">
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Find a person or title…"
                  className="px-2 py-1 border rounded w-52 text-xs"
                />
                <span className="flex items-center gap-1 text-gray-500">
                  Levels:
                  {[2, 3, 4].filter(nn => nn < maxDepth).map(nn => (
                    <button key={nn} onClick={() => applyDepthPreset(nn)}
                      className="px-1.5 py-0.5 bg-white border border-gray-300 rounded hover:bg-indigo-50 hover:border-indigo-300"
                      title={`Collapse everything below level ${nn} (e.g. ${nn === 2 ? 'exec team only' : `top ${nn} tiers`})`}>Top {nn}</button>
                  ))}
                  <button onClick={() => applyDepthPreset(null)}
                    className="px-1.5 py-0.5 bg-white border border-gray-300 rounded hover:bg-indigo-50 hover:border-indigo-300"
                    title="Expand the whole company">All</button>
                </span>
                <span className="text-gray-500">{placed.length} placed</span>
                <span className="inline-flex items-center gap-1 text-amber-600" title="Reasonable structural guesses — hover a dot for the reason"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> {confCounts.medium}</span>
                <span className="inline-flex items-center gap-1 text-red-500" title="Low confidence — check these first"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> {confCounts.low}</span>
                {confCounts.pinned > 0 && <span className="text-indigo-600" title="Confirmed by you">📌 {confCounts.pinned}</span>}
                {(chart.notes || []).length > 0 && (
                  <button onClick={() => setShowNotes(s => !s)} className="ml-auto text-indigo-600 hover:underline">
                    {showNotes ? 'Hide' : 'Show'} AI notes ({(chart.notes || []).length})
                  </button>
                )}
              </div>

              {showNotes && (
                <div className="px-5 py-2 border-b bg-indigo-50/50 max-h-36 overflow-y-auto shrink-0">
                  {(chart.notes || []).map((n, i) => <p key={i} className="text-xs text-gray-700 py-0.5">• {n}</p>)}
                </div>
              )}

              {/* Canvas */}
              <div
                ref={viewportRef}
                className="flex-1 overflow-hidden bg-gray-50 relative cursor-grab active:cursor-grabbing"
                style={{ backgroundImage: 'radial-gradient(circle, #d7dce3 1px, transparent 1px)', backgroundSize: '22px 22px' }}
                onWheel={onWheel}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                onDoubleClick={e => {
                  const rect = viewportRef.current?.getBoundingClientRect();
                  zoomAt(1.6, rect ? e.clientX - rect.left : undefined, rect ? e.clientY - rect.top : undefined);
                }}
              >
                <div style={{ transform: `translate(${view.px}px, ${view.py}px) scale(${view.z})`, transformOrigin: '0 0', width: layout.width, height: layout.height, position: 'relative' }}>
                  <svg width={layout.width} height={layout.height} className="absolute inset-0 pointer-events-none overflow-visible">
                    {layout.edges.map((d, i) => (
                      <path key={i} d={d} fill="none" stroke="#9aa4b2" strokeWidth={1.5} />
                    ))}
                  </svg>
                  {layout.cards.map(renderCard)}
                </div>

                <div className="absolute bottom-4 right-4 flex flex-col gap-1 bg-white rounded-lg shadow border border-gray-200 p-1">
                  <button onClick={() => zoomAt(1.25)} className="w-8 h-8 text-gray-600 hover:bg-gray-100 rounded text-sm font-semibold">+</button>
                  <button onClick={() => zoomAt(1 / 1.25)} className="w-8 h-8 text-gray-600 hover:bg-gray-100 rounded text-sm font-semibold">−</button>
                  <button onClick={fit} className="w-8 h-8 text-gray-600 hover:bg-gray-100 rounded text-[9px] font-medium" title="Fit the whole chart on screen">FIT</button>
                </div>
                <div className="absolute bottom-4 left-4 text-[10px] text-gray-400 bg-white/80 rounded px-2 py-1 pointer-events-none">
                  drag the canvas to pan · scroll or double-click to zoom · click a card for the profile · drag a card onto someone to change their manager
                </div>
              </div>

              {/* Unplaced tray */}
              {unplaced.length > 0 && (
                <div className="border-t shrink-0 bg-white">
                  <button onClick={() => setShowUnplaced(s => !s)} className="w-full px-5 py-2 text-left text-xs font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-2">
                    <span>{showUnplaced ? '▾' : '▸'}</span>
                    Not in the org ({unplaced.length}) — duplicates, board members, unrelated companies, stale profiles
                  </button>
                  {showUnplaced && (
                    <div className="max-h-44 overflow-y-auto px-5 pb-3">
                      {unplaced.map(n => (
                        <div key={n.candidate_id} className="group flex items-center gap-2 py-1 text-xs">
                          <Avatar n={n} size={22} />
                          <button onClick={() => openProfile(n)} className="font-medium text-gray-700 hover:text-indigo-700 hover:underline">{name(n)}</button>
                          <span className="text-gray-400 truncate">{chartTitle(n)}</span>
                          {n.unplaced_why && <span className="text-gray-400 italic truncate">— {n.unplaced_why}</span>}
                          {n.pinned && <span className="text-[10px]">📌</span>}
                          <button
                            onClick={() => setPicker({ node: n, query: '' })}
                            disabled={saving}
                            className="ml-auto hidden group-hover:block px-1.5 py-0.5 text-[10px] bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 shrink-0"
                          >Place under…</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {renderProfile()}

        {/* Manager picker */}
        {picker && (
          <div className="fixed inset-0 bg-black/30 z-[70] flex items-center justify-center p-4" onClick={() => setPicker(null)}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="px-4 py-3 border-b">
                <p className="text-sm font-medium text-gray-900">Who does {name(picker.node)} report to?</p>
                <input
                  autoFocus
                  value={picker.query}
                  onChange={e => setPicker({ ...picker, query: e.target.value })}
                  placeholder="Search by name or title…"
                  className="mt-2 w-full px-2 py-1.5 border rounded text-sm"
                />
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {pickerOptions.map(opt => (
                  <button
                    key={opt.candidate_id}
                    onClick={() => { const node = picker.node; setPicker(null); patchNode(node.id, { parentCandidateId: opt.candidate_id }); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-indigo-50 text-left"
                  >
                    <Avatar n={opt} size={24} />
                    <span className="text-sm text-gray-900">{name(opt)}</span>
                    <span className="text-xs text-gray-500 truncate">{chartTitle(opt)}</span>
                  </button>
                ))}
                {!pickerOptions.length && <p className="p-3 text-xs text-gray-400">No matches.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgChartModal;
